import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { createCheckoutSession } from "../../services/paymentsService";
import styles from "./PaymentCancelled.module.css";
import type { Plan } from "../../types/payments";

const PaymentCancelled: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRetryPayment = async () => {
    if (!selectedPlan) {
      setError("Please select a plan to continue");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await createCheckoutSession(selectedPlan);
      
      // Use the checkout URL directly instead of redirectToCheckout
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error("No checkout URL returned from server.");
      }
    } catch (err: any) {
      console.error("Payment retry error:", err);
      setError(err?.message || "Failed to initialize payment. Please try again.");
      setLoading(false);
    }
  };

  const handleContinueFree = () => {
    // Navigate to onboarding complete with free plan
    navigate("/onboarding-complete", { replace: true });
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.iconWrapper}>
          ⚠️
        </div>

        <h1 className={styles.heading}>Payment Cancelled</h1>

        <p className={styles.message}>
          Your payment was not completed. Don&apos;t worry, you haven&apos;t been charged.
        </p>

        <div className={styles.infoBox}>
          <div className={styles.infoTitle}>
            <span className={styles.infoIcon}>ℹ️</span>
            What happened?
          </div>
          <div className={styles.infoText}>
            The payment process was cancelled. This could be because:
          </div>
          <ul className={styles.infoList}>
            <li>You clicked the back button or closed the payment window</li>
            <li>Your payment method was declined</li>
            <li>There was a technical issue with the payment processor</li>
          </ul>
        </div>

        <div className={styles.divider}></div>

        <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "#1f2937" }}>
          What would you like to do?
        </h3>

        <div className={styles.planOptions}>
          <div
            className={`${styles.planCard} ${selectedPlan === "basic" ? styles.planCardSelected : ""}`}
            onClick={() => setSelectedPlan("basic")}
          >
            <div className={styles.planCardName}>Basic Plan</div>
            <div className={styles.planCardPrice}>$50<span style={{ fontSize: "1rem", fontWeight: "normal" }}>/mo</span></div>
            <div className={styles.planCardFeatures}>
              Unlimited sends &amp; automations
            </div>
          </div>

          <div
            className={`${styles.planCard} ${selectedPlan === "pro" ? styles.planCardSelected : ""}`}
            onClick={() => setSelectedPlan("pro")}
          >
            <div className={styles.planCardName}>Pro Plan</div>
            <div className={styles.planCardPrice}>$75<span style={{ fontSize: "1rem", fontWeight: "normal" }}>/mo</span></div>
            <div className={styles.planCardFeatures}>
              Unlimited sends, automations, data room &amp; decks
            </div>
          </div>
        </div>

        {error && (
          <div style={{ 
            background: "#fef2f2", 
            border: "1px solid #fecaca", 
            borderRadius: "8px", 
            padding: "1rem", 
            marginBottom: "1rem", 
            color: "#991b1b",
            fontSize: "0.9375rem"
          }}>
            {error}
          </div>
        )}

        <div className={styles.buttonGroup}>
          <button
            onClick={handleRetryPayment}
            disabled={!selectedPlan || loading}
            className={`${styles.button} ${styles.buttonPrimary}`}
          >
            {loading ? "Processing..." : "Retry Payment"}
          </button>

          <button
            onClick={handleContinueFree}
            disabled={loading}
            className={`${styles.button} ${styles.buttonOutline}`}
          >
            Continue with Free Plan
          </button>
        </div>

        <div className={styles.divider}></div>

        <div className={styles.buttonGroup}>
          <button
            onClick={() => navigate("/onboarding", { replace: true })}
            disabled={loading}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            ← Back to Onboarding
          </button>
        </div>

        <div className={styles.helpSection}>
          <div className={styles.helpTitle}>Need Help?</div>
          <p className={styles.helpText}>
            If you&apos;re experiencing payment issues or have questions about our plans, we&apos;re here to assist you.
          </p>
          <a href="mailto:support@sendupdates.ai" className={styles.helpLink}>
            Contact Support →
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentCancelled;

